/**
 * RegisterGrid — the editable grid for one table of a statutory register.
 *
 * Registers are wide. MGT-1 has 23 columns, SH-3 has 21, and every one of them
 * is prescribed, so none can be dropped to make the screen tidier. The grid
 * therefore scrolls horizontally inside its own container with a sticky row
 * gutter, rather than wrapping or hiding columns behind a "show more".
 *
 * Editing is in-place and saves on blur. That matches how companies keep these
 * today — in a spreadsheet — and avoids a modal round-trip per cell across a
 * 20-column form. A cell only saves if its value actually changed, so tabbing
 * across a row costs nothing.
 *
 * Two-level headers are rendered from the same `headerSpans` logic the export
 * uses, so what you see on screen is what comes out of the workbook: grouped
 * columns share a merged parent cell, and the statutory (1)…(n) numbering row
 * sits beneath them.
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { TbTrash, TbPlus, TbLock, TbLockOpen } from 'react-icons/tb';

/* ── Header spans, mirroring backend/utils/registerSchemas.js ────────────── */

/**
 * Collapse `group` into header spans. Only CONSECUTIVE columns merge — MGT-1
 * has two separate "Distinctive numbers" groups that must stay two groups.
 */
export function headerSpans(columns) {
  const spans = [];
  (columns || []).forEach((col, i) => {
    const prev = spans[spans.length - 1];
    if (col.group && prev && prev.grouped && prev.label === col.group && prev.start + prev.span === i) {
      prev.span += 1;
      return;
    }
    spans.push({ label: col.group || col.label, span: 1, start: i, grouped: Boolean(col.group) });
  });
  return spans;
}

/* ── Column widths ───────────────────────────────────────────────────────── */

/**
 * Width per column type. A statutory form is wide whatever we do — CHG-7 has 17
 * prescribed columns and none may be dropped — but giving a date the same 150px
 * as a paragraph of terms and conditions made it needlessly wider still, and
 * pushed everything off screen sooner.
 */
const TYPE_WIDTH = { date: 110, number: 114, money: 124, text: 150, textarea: 172 };

const GUTTER = 44;

const widthOf = (column) => {
  // The serial column holds "1", "2", "3" in every form that has one.
  if (column.key === 'srNo') return 66;
  return TYPE_WIDTH[column.type] || TYPE_WIDTH.text;
};

/* ── Cell chrome ─────────────────────────────────────────────────────────── */

const cellBase =
  'w-full h-full px-2 py-1.5 text-[12px] text-[#08111f] bg-transparent outline-none ' +
  'placeholder:text-[#c4bbaa] focus:bg-white focus:shadow-[inset_0_0_0_2px_rgba(20,95,95,0.35)]';

const thBase =
  'sticky top-0 z-10 bg-[#f4f1e9] border border-[#ddd6c6] px-2 py-1.5 text-[11px] font-bold ' +
  'text-[#4a4335] leading-tight align-bottom';

/**
 * One editable cell. Local state while focused so typing is not fighting the
 * server round-trip; committed to the parent on blur, and only when changed.
 */
function Cell({ column, value, readOnly, onCommit }) {
  const [draft, setDraft] = useState(value ?? '');
  const dirty = useRef(false);

  // A save elsewhere in the row recomputes balances, so an unfocused cell must
  // follow the server's value rather than keep a stale local copy.
  useEffect(() => {
    if (!dirty.current) setDraft(value ?? '');
  }, [value]);

  if (readOnly) {
    return (
      <div className={`${cellBase} text-[#7a7060] italic tabular-nums`} title="Kept by the register">
        {value || ''}
      </div>
    );
  }

  const commit = () => {
    dirty.current = false;
    if ((draft ?? '') !== (value ?? '')) onCommit(draft);
  };

  const numeric = column.type === 'number' || column.type === 'money';

  if (column.type === 'textarea') {
    return (
      <textarea
        className={`${cellBase} resize-none min-h-[34px] leading-snug`}
        rows={1}
        value={draft}
        onChange={(e) => { dirty.current = true; setDraft(e.target.value); }}
        onBlur={commit}
      />
    );
  }

  return (
    <input
      type="text"
      inputMode={numeric ? 'decimal' : undefined}
      className={`${cellBase} ${numeric ? 'text-right tabular-nums' : ''}`}
      // Dates are typed, not picked: a register transcribes what was recorded,
      // and "1/10/25" from an old paper book must survive being entered as-is.
      placeholder={column.type === 'date' ? 'DD/MM/YYYY' : ''}
      value={draft}
      onChange={(e) => { dirty.current = true; setDraft(e.target.value); }}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
    />
  );
}

/* ── The grid ────────────────────────────────────────────────────────────── */

export default function RegisterGrid({
  columns,
  rows,
  canEdit,
  onAddRow,
  onUpdateRow,
  onDeleteRow,
  onToggleOverride,
  emptyLabel = 'No entries yet.',
}) {
  const spans = useMemo(() => headerSpans(columns), [columns]);
  const hasSubRow = spans.some((s) => s.grouped);
  const hasNumbering = columns.some((c) => c.no);
  const headerRows = 1 + (hasSubRow ? 1 : 0) + (hasNumbering ? 1 : 0);

  const total = useMemo(
    () => GUTTER + columns.reduce((n, c) => n + widthOf(c), 0) + (canEdit ? GUTTER : 0),
    [columns, canEdit],
  );

  return (
    <div className="rounded-xl border-[1.5px] border-[#ddd6c6] bg-white overflow-hidden">
      <div className="overflow-x-auto">
        {/* table-layout:fixed so the colgroup widths hold and long statutory
            headers wrap instead of stretching one column to fit. */}
        <table className="border-collapse" style={{ width: total, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: GUTTER }} />
            {columns.map((c) => (
              <col key={c.key} style={{ width: widthOf(c) }} />
            ))}
            {canEdit && <col style={{ width: GUTTER }} />}
          </colgroup>

          <thead>
            <tr>
              <th className={`${thBase} text-center align-middle sticky left-0 z-20`} rowSpan={headerRows}>
                #
              </th>
              {spans.map((span) => (
                <th
                  key={`${span.label}-${span.start}`}
                  className={thBase}
                  colSpan={span.span}
                  rowSpan={span.grouped || !hasSubRow ? 1 : 2}
                >
                  {span.label}
                </th>
              ))}
              {canEdit && <th className={thBase} rowSpan={headerRows} />}
            </tr>

            {hasSubRow && (
              <tr>
                {spans.filter((s) => s.grouped).flatMap((span) =>
                  Array.from({ length: span.span }, (unused, i) => (
                    <th key={columns[span.start + i].key} className={thBase}>
                      {columns[span.start + i].label}
                    </th>
                  )),
                )}
              </tr>
            )}

            {hasNumbering && (
              <tr>
                {columns.map((c) => (
                  <th key={`no-${c.key}`} className={`${thBase} text-center font-normal text-[#8b8371]`}>
                    {c.no || ''}
                  </th>
                ))}
              </tr>
            )}
          </thead>

          <tbody>
            {rows.length === 0 && (
              <tr>
                {/* The message is pinned to the left edge rather than centred.
                    Centring it across 17 prescribed columns put it ~1300px to
                    the right, so an empty register looked like a blank band
                    with no explanation until you scrolled sideways to find it. */}
                <td
                  colSpan={columns.length + 1 + (canEdit ? 1 : 0)}
                  className="border border-[#ddd6c6] p-0"
                >
                  <div className="sticky left-0 w-[min(560px,100%)] px-4 py-7 text-[12px] text-[#7a7060]">
                    {emptyLabel}
                  </div>
                </td>
              </tr>
            )}

            {rows.map((row, index) => (
              <tr key={row.id} className="hover:bg-[#faf8f3]">
                <td className="sticky left-0 z-[5] bg-white border border-[#ddd6c6] text-center text-[11px] text-[#8b8371] tabular-nums">
                  {index + 1}
                </td>

                {columns.map((column) => {
                  const overridden = (row.overrides || []).includes(column.key);
                  const derived = Boolean(column.computed) && !overridden;
                  return (
                    <td
                      key={column.key}
                      className={`border border-[#ddd6c6] p-0 align-top ${derived ? 'bg-[#f7f5ef]' : ''}`}
                    >
                      <div className="flex items-stretch">
                        <div className="flex-1 min-w-0">
                          <Cell
                            column={column}
                            value={row.data[column.key] || ''}
                            readOnly={derived || !canEdit}
                            onCommit={(next) => onUpdateRow(row, { [column.key]: next })}
                          />
                        </div>
                        {/* A running balance is derived from the rows above it.
                            Taking it over by hand is legitimate — an opening
                            balance from a paper book — but it is shown, never
                            silent. */}
                        {column.computed && canEdit && (
                          <button
                            type="button"
                            onClick={() => onToggleOverride(row, column.key)}
                            title={derived ? 'Kept by the register — click to enter by hand' : 'Entered by hand — click to let the register keep it'}
                            className="px-1 text-[#a39a86] hover:text-[#145f5f] shrink-0"
                          >
                            {derived ? <TbLock size={13} /> : <TbLockOpen size={13} />}
                          </button>
                        )}
                      </div>
                    </td>
                  );
                })}

                {canEdit && (
                  <td className="border border-[#ddd6c6] text-center">
                    <button
                      type="button"
                      onClick={() => onDeleteRow(row)}
                      title="Delete this entry"
                      className="p-1.5 text-[#a39a86] hover:text-[#b3261e]"
                    >
                      <TbTrash size={14} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canEdit && (
        <button
          type="button"
          onClick={onAddRow}
          className="w-full flex items-center justify-center gap-1.5 py-2.5 text-[12px] font-semibold
                     text-[#145f5f] hover:bg-[#f2f7f6] border-t-[1.5px] border-[#ddd6c6]"
        >
          <TbPlus size={14} /> Add entry
        </button>
      )}
    </div>
  );
}
